import http from '@/plugins/axios.instance';
import resetMapper from '@/http/reset.mapper';
import resetService from '@/services/reset.service';
import { CxServiceUnavailableError } from '@/classes/error.class';
import { Password } from '@/interfaces/password.interface';

const recoveryUrl = process.env.VUE_APP_RECOVERY_URL || '/password_reset/recover/';
const resetUrl = process.env.VUE_APP_RESET_URL || '/password_reset/reset/';

/**
 * Call to generate password reset link
 */
const recoverPassword = async (username: string): Promise<string> => {

  const getCsrfToken = async (): Promise<string | null> => {
    let tokenHtmlResponse;
    try {
      tokenHtmlResponse = await http.get(recoveryUrl);
    } catch (error) {
      throw new CxServiceUnavailableError(error);
    }
    return resetMapper.extractCsrfToken(tokenHtmlResponse);
  };

  const postRecoverPassword = async (csrfToken: string | null): Promise<string> => {
    if (!csrfToken) throw new Error('Password Recovery Failed - csrf token cannot be null or empty');
    const formData = new FormData();
    formData.append('username_or_email', username);
    formData.append('csrfmiddlewaretoken', csrfToken);
    let resetResponse;
    try {
      resetResponse = await http.post(recoveryUrl, formData);
    } catch (error) {
      throw new CxServiceUnavailableError(error);
    }
    return resetService.parseErrors(resetResponse);
  };

  const csrfToken: string | null = await getCsrfToken();
  return postRecoverPassword(csrfToken);
};

/**
 * Call to validate password reset token generated by the backend
 * @returns string with username if it is present, or null if token is invalid or username could not be found
 */
const validateResetToken = async (hash: string | string[], uidb64: string | string[]): Promise<string | null> => {
  const validationUrl = `${resetUrl}${hash}/${uidb64}`;
  let tokenHtmlResponse;
  try {
    tokenHtmlResponse = await http.get(validationUrl);
  } catch (error) {
    throw new CxServiceUnavailableError(error);
  }
  const isValid = resetService.validateTokenResponse(tokenHtmlResponse);
  return isValid ? resetMapper.extractUsername(tokenHtmlResponse) : null;
};

/**
 * Call to set new password
 */
const resetPassword = async (newPassword: Password, hash: string | string[] | null, uidb64: string | string[] | null): Promise<boolean> => {

  const getResetPasswordCsrfToken = async (): Promise<string | null> => {
    const resetTokenUrl = `${resetUrl}${hash}/${uidb64}`;
    let tokenHtmlResponse;
    try {
      tokenHtmlResponse = await http.get(resetTokenUrl);
    } catch (error) {
      throw new CxServiceUnavailableError(error);
    }
    return resetMapper.extractCsrfToken(tokenHtmlResponse);
  };

  const postNewPassword = async (csrfToken: string | null): Promise<boolean> => {
    if (!csrfToken) throw new Error('Reset Password Failed - csrf token cannot be null or empty');
    const postNewPasswordUrl = `${resetUrl}set-password/${uidb64}/`;
    const { password1, password2 } = newPassword;
    const encodeUrl = (data: Record<string, string>) => Object.keys(data).reduce((acc, key) => `${acc}${key}=${encodeURIComponent(data[key])}&`, '');
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    /* eslint-disable @typescript-eslint/camelcase */
    const data = encodeUrl({
      csrfmiddlewaretoken: csrfToken,
      new_password1: password1,
      new_password2: password2,
    });
    /* eslint-enable @typescript-eslint/camelcase */
    let newPasswordResponse;
    try {
      newPasswordResponse = await http.post(postNewPasswordUrl, data, { headers });
    } catch (error) {
      throw new CxServiceUnavailableError(error);
    }
    return resetService.validateNewPasswordResponse(newPasswordResponse);
  };

  const csrfToken: string | null = await getResetPasswordCsrfToken();
  const resetPasswordResponse = await postNewPassword(csrfToken);
  return resetPasswordResponse;
};

const resetApi = { recoverPassword, validateResetToken, resetPassword };

export default resetApi;
